/**
 * 日落橙模版 - 页脚
 * 
 * 设计理念：3栏 + 品牌横幅布局
 * - 深暖色背景，搭配琥珀金渐变顶线
 * - 品牌区占全宽（顶部横幅），下方3栏网格
 * - 圆润的图标容器，区别于teal的4栏和red的3栏
 */

import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function SunsetFooter() {
  const { siteConfig } = useSiteConfig();

  const phone = siteConfig?.contact_info?.phone || '';
  const email = siteConfig?.contact_info?.email || '';
  const address = siteConfig?.contact_info?.address;
  const companyName = siteConfig?.contact_info?.company_name;
  const icp = siteConfig?.icp || '';
  const icpUrl = siteConfig?.icp_url || '';
  const displayName = siteConfig?.site_name || '';
  const siteName = siteConfig?.name || '';
  const logo = siteConfig?.logo || '';
  const footerInfo = siteConfig?.footer_info || '';

  const defaultMenus = [
    { id: "m1", label: "首页", href: "/", external: false, enabled: true },
    { id: "m2", label: "发布信息", href: "/publish", external: false, enabled: true },
    { id: "m3", label: "开通VIP", href: "/vip", external: false, enabled: true },
    { id: "m4", label: "会员中心", href: "/dashboard", external: false, enabled: true },
    { id: "m5", label: "帮助中心", href: "/page/help", external: false, enabled: true },
    { id: "m6", label: "关于我们", href: "/page/about", external: false, enabled: true },
    { id: "m7", label: "隐私政策", href: "/page/privacy", external: false, enabled: true },
    { id: "m8", label: "服务协议", href: "/page/register-agreement", external: false, enabled: true },
  ];

  const hasFooterMenus = siteConfig?.footer_menus && Array.isArray(siteConfig.footer_menus) && siteConfig.footer_menus.length > 0;
  const menus = hasFooterMenus
    ? siteConfig.footer_menus.filter((m: any) => m.enabled !== false)
    : defaultMenus;

  const currentYear = new Date().getFullYear();
  const copyrightOwner = companyName || displayName || siteName;
  const copyright = copyrightOwner ? `\u00a9 ${currentYear} ${copyrightOwner}` : `\u00a9 ${currentYear}`;

  const brandName = displayName || siteName;

  const quickMenus = menus.slice(0, 4);
  const moreMenus = menus.slice(4, 8);

  return (
    <footer className="bg-[#1c1610] text-gray-400">
      {/* 渐变顶线 */}
      <div className="h-1 bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {/* ── 顶部品牌横幅 ── */}
        <div className="mb-12 pb-10 border-b border-white/10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              {logo ? (
                <img src={logo} alt={brandName} className="h-9 w-9 object-contain rounded-xl" />
              ) : (
                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold shadow-lg shadow-amber-500/20">
                  {brandName ? brandName.charAt(0) : 'S'}
                </div>
              )}
              <div>
                {brandName && <span className="text-xl font-bold text-white">{brandName}</span>}
                <p className="text-xs text-gray-500 mt-0.5">
                  {footerInfo || '本地分类信息平台，汇聚各类生活服务信息'}
                </p>
              </div>
            </div>
            {/* 社交媒体 */}
            <div className="flex items-center gap-3">
              {phone && (
                <a href={`tel:${phone}`} className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-amber-500/20 hover:text-amber-400 transition-colors" title="电话联系">
                  <i className="ri-phone-line text-lg" />
                </a>
              )}
              {email && (
                <a href={`mailto:${email}`} className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center hover:bg-amber-500/20 hover:text-amber-400 transition-colors" title="发送邮件">
                  <i className="ri-mail-line text-lg" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* ── 3栏网格 ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {/* 栏目1: 快捷导航 */}
          <div>
            <h4 className="text-sm font-semibold text-amber-400 mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full inline-block" />
              快捷导航
            </h4>
            <ul className="space-y-3">
              {quickMenus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm hover:text-amber-400 transition-colors flex items-center gap-2">
                      <i className="ri-arrow-right-s-line text-xs opacity-0 group-hover:opacity-100" />
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm hover:text-amber-400 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* 栏目2: 更多服务 */}
          <div>
            <h4 className="text-sm font-semibold text-amber-400 mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full inline-block" />
              更多服务
            </h4>
            <ul className="space-y-3">
              {moreMenus.length > 0 ? moreMenus.map((item: any) => (
                <li key={item.id}>
                  {item.external ? (
                    <a href={item.href} target="_blank" rel="nofollow noopener noreferrer"
                      className="text-sm hover:text-amber-400 transition-colors">
                      {item.label}
                    </a>
                  ) : (
                    <Link to={item.href} className="text-sm hover:text-amber-400 transition-colors">
                      {item.label}
                    </Link>
                  )}
                </li>
              )) : (
                <li className="text-sm text-gray-500">暂无更多</li>
              )}
            </ul>
          </div>

          {/* 栏目3: 联系方式 */}
          <div>
            <h4 className="text-sm font-semibold text-amber-400 mb-5 uppercase tracking-wider flex items-center gap-2">
              <span className="w-1 h-4 bg-gradient-to-b from-amber-400 to-orange-500 rounded-full inline-block" />
              联系我们
            </h4>
            <ul className="space-y-3">
              {phone && (
                <li>
                  <a href={`tel:${phone}`}
                    className="flex items-center gap-3 text-sm hover:text-amber-400 transition-colors group">
                    <span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors shrink-0">
                      <i className="ri-phone-line text-amber-400" />
                    </span>
                    {phone}
                  </a>
                </li>
              )}
              {email && (
                <li>
                  <a href={`mailto:${email}`}
                    className="flex items-center gap-3 text-sm hover:text-amber-400 transition-colors group">
                    <span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center group-hover:bg-amber-500/20 transition-colors shrink-0">
                      <i className="ri-mail-line text-amber-400" />
                    </span>
                    {email}
                  </a>
                </li>
              )}
              {address && (
                <li>
                  <div className="flex items-start gap-3 text-sm group">
                    <span className="w-9 h-9 rounded-xl bg-white/5 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-map-pin-line text-amber-400" />
                    </span>
                    <span>{address}</span>
                  </div>
                </li>
              )}
              {!phone && !email && !address && (
                <li className="text-sm text-gray-500">暂无联系方式</li>
              )}
            </ul>
          </div>
        </div>
      </div>

      {/* 底栏 */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-gray-500">{copyright}</span>
          <div className="flex items-center gap-4">
            {icp && (
              <a href={icpUrl} target="_blank" rel="nofollow noopener noreferrer"
                className="text-xs text-gray-500 hover:text-amber-400 transition-colors">
                {icp}
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
